/**
 * Spawn objects from the cloud library into the current scene.
 *
 * Blend-sourced items are downloaded as raw .blend files and fed through
 * `addBlendFileParsed` — the same path as "Add Blend File" from disk —
 * preserving materials, shape keys, hierarchy, visibility, modifiers,
 * and drivers at full fidelity.
 *
 * Mesh-file-sourced items are stored as .pr files and deserialized as
 * SceneObjects or scene Collections, depending on the library envelope.
 */

import type { LibraryItemResponse } from "@poseref/shared";
import type { SceneObject } from "../modeling/types.ts";
import { createSceneObjectId } from "../modeling/types.ts";
import { createCollectionId } from "./collectionIds.ts";
import { computeFileHash } from "./fileHash.ts";
import { decodeLibraryObject, parseLibraryFile } from "./libraryIO.ts";
import { clearLiveEditPreview } from "./liveEditPreview.ts";
import type {
  LibraryCollectionDocument,
  LibraryObjectDocument,
} from "./persistence/types.ts";
import { addSceneObjects } from "./sceneObjectMap.ts";
import { useStore } from "./store.ts";
import type { Collection, InteractionMode } from "./storeTypes.ts";
import { IDENTITY_GROUP_TRANSFORM } from "./storeTypes.ts";

interface PreparedLibrarySpawn {
  objects: SceneObject[];
  collection?: Collection;
}

/**
 * Download and spawn a library object or collection into the current scene.
 * Returns the IDs of the spawned objects (empty for blend items, which live
 * in the blend object model rather than sceneObjects).
 */
export async function spawnFromCloudLibrary(
  item: LibraryItemResponse,
): Promise<string[]> {
  if (!item.fileUrl) throw new Error("Library item has no file URL");
  if (item.type === "scene")
    throw new Error("Cannot spawn a scene — use openSceneFromLibrary instead");

  // Blend items: download raw .blend and merge via addBlendFileParsed
  if (item.type === "blend") {
    const res = await fetch(item.fileUrl);
    if (!res.ok) throw new Error(`Failed to download: ${res.statusText}`);
    const buffer = await res.arrayBuffer();
    const hash = await computeFileHash(buffer);
    const store = useStore.getState();
    // Switch to object mode if not already there
    if (store.workspaceMode !== "object") {
      store.setWorkspaceMode("object");
    }
    await store.addBlendFileParsed(buffer, hash, item.name);
    return [];
  }

  // Mesh-file library items: download .pr and deserialize into scene content.
  const res = await fetch(item.fileUrl);
  if (!res.ok) throw new Error(`Failed to download: ${res.statusText}`);
  const text = await res.text();

  const file = parseLibraryFile(text);

  const store = useStore.getState();
  const existingNames = new Set(
    [...store.sceneObjects.values()].map((o) => o.name),
  );
  const existingCollectionNames = new Set(
    [...store.collections.values()].map((collection) => collection.name),
  );

  if (file.kind === "object") {
    const f = file as LibraryObjectDocument;
    const spawn = item.sourceFileUrl
      ? prepareSourceImportedObjectSpawn(
          item.name,
          f,
          existingNames,
          existingCollectionNames,
        )
      : prepareObjectSpawn(f, existingNames);
    applySpawnToScene(spawn);
    return spawn.objects.map((obj) => obj.id);
  } else if (file.kind === "collection") {
    const f = file as LibraryCollectionDocument;
    const spawn = prepareCollectionSpawn(
      item.name,
      f,
      existingNames,
      existingCollectionNames,
    );
    applySpawnToScene(spawn);
    return spawn.objects.map((obj) => obj.id);
  }

  return [];
}

/** Assign a new unique ID and deduplicate the name. */
function assignFreshId(
  obj: SceneObject,
  existingNames: Set<string>,
): SceneObject {
  obj.id = createSceneObjectId();

  obj.name = dedupeBlenderName(obj.name, existingNames, "Object");
  existingNames.add(obj.name);
  return obj;
}

function prepareObjectSpawn(
  file: LibraryObjectDocument,
  existingObjectNames: Set<string>,
): PreparedLibrarySpawn {
  const obj = assignFreshId(
    decodeLibraryObject(file.object),
    existingObjectNames,
  );
  return { objects: [obj] };
}

/**
 * Backward-compatibility path for older imported single-mesh assets that were
 * stored as plain `kind: "object"` items before file-backed mesh imports were
 * standardized on collection envelopes.
 */
export function prepareSourceImportedObjectSpawn(
  itemName: string,
  file: LibraryObjectDocument,
  existingObjectNames: Set<string>,
  existingCollectionNames: Set<string>,
): PreparedLibrarySpawn {
  const obj = assignFreshId(
    decodeLibraryObject(file.object),
    existingObjectNames,
  );
  const worldPosition = [...obj.position] as [number, number, number];
  obj.position = [0, 0, 0];
  return wrapObjectsInCollection(
    itemName,
    [obj],
    worldPosition,
    existingCollectionNames,
  );
}

export function prepareCollectionSpawn(
  itemName: string,
  file: LibraryCollectionDocument,
  existingObjectNames: Set<string>,
  existingCollectionNames: Set<string>,
): PreparedLibrarySpawn {
  const objects = file.objects.map((serialized) =>
    assignFreshId(decodeLibraryObject(serialized), existingObjectNames),
  );
  return wrapObjectsInCollection(
    itemName,
    objects,
    file.centroid,
    existingCollectionNames,
  );
}

function wrapObjectsInCollection(
  requestedName: string,
  objects: SceneObject[],
  position: [number, number, number],
  existingCollectionNames: Set<string>,
): PreparedLibrarySpawn {
  const name = dedupeBlenderName(
    requestedName,
    existingCollectionNames,
    "Collection",
  );
  existingCollectionNames.add(name);

  return {
    objects,
    collection: {
      id: createCollectionId(),
      name,
      transform: {
        position: [...position] as [number, number, number],
        rotation: [...IDENTITY_GROUP_TRANSFORM.rotation] as [
          number,
          number,
          number,
          number,
        ],
        scale: [...IDENTITY_GROUP_TRANSFORM.scale] as [number, number, number],
      },
      objectIds: new Set(objects.map((obj) => obj.id)),
    },
  };
}

export function dedupeBlenderName(
  requestedName: string,
  usedNames: ReadonlySet<string>,
  fallbackName: string,
): string {
  const baseName = requestedName.trim() || fallbackName;
  if (!usedNames.has(baseName)) return baseName;

  for (let i = 1; i < 10_000; i++) {
    const candidate = `${baseName}.${String(i).padStart(3, "0")}`;
    if (!usedNames.has(candidate)) return candidate;
  }
  return `${baseName}.${Date.now()}`;
}

/** Add a prepared spawn payload to the scene and select its top-level target. */
function applySpawnToScene(spawn: PreparedLibrarySpawn): void {
  const store = useStore.getState();

  // Switch to object mode if not already there
  if (store.workspaceMode !== "object") {
    store.setWorkspaceMode("object");
  }

  const spawnAdd = addSceneObjects(store.sceneObjects, spawn.objects);
  if (!spawnAdd.admitted) return;
  const collections = spawn.collection
    ? new Map(store.collections).set(spawn.collection.id, spawn.collection)
    : store.collections;

  clearLiveEditPreview();
  useStore.setState({
    sceneObjects: spawnAdd.map,
    collections,
    selectedObjectTargets: spawn.collection
      ? [{ kind: "collection", id: spawn.collection.id }]
      : spawn.objects.map((o) => ({ kind: "sceneObject", id: o.id })),
    mode: "transform" as InteractionMode,
    editMode: null,
  });
}
